
import { useAuthContext } from '@/contexts/auth';
import { useState, useEffect } from 'react';
import { debugLog, debugError } from '@/utils/debugLogger';
import { waitForHydrationComplete } from '@/utils/hydrationManager';
import { supabase } from '@/lib/supabase/client';
import { deduplicateRequest } from '@/lib/utils/requestDeduplication';

export const useAuth = () => {
  const auth = useAuthContext();
  const { user, isAuthenticated, isLoading } = auth;
  
  const [role, setRole] = useState<string>('user');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isDealer, setIsDealer] = useState(false);
  const [loading, setLoading] = useState(true);

  // 🛡️ Wait for hydration, then verify roles via server-side RPC
  // isAdmin/isSuperAdmin are verified server-side to prevent client-state manipulation
  // 🛡️ RPC calls are deduplicated to prevent 4x is_admin / 3x is_super_admin at startup
  useEffect(() => {
    let cancelled = false;
    
    waitForHydrationComplete().then(async () => {
      if (cancelled) return;
      
      try {
        debugLog('auth', 'Auth state:', { isAuthenticated, user, isLoading }, 'useAuth');
        
        if (isAuthenticated && user) {
          debugLog('auth', `Authenticated user, checking role for: ${user.id}`, undefined, 'useAuth');
          
          // Non-security-critical: derive role/isDealer from client state (for UI routing only)
          const userRole = user.role || '';
          const normalizedRole = typeof userRole === 'string' 
            ? userRole.toLowerCase() 
            : 'user';
          
          setRole(normalizedRole);
          setIsDealer(normalizedRole === 'dealer');
          
          // 🛡️ SECURITY: Verify admin flags via server-side RPC (SECURITY DEFINER)
          // Deduplicated: multiple useAuth instances share the same in-flight request
          const [adminResult, superAdminResult] = await Promise.all([
            deduplicateRequest('rpc:is_admin', async () => await supabase.rpc('is_admin')),
            deduplicateRequest('rpc:is_super_admin', async () => await supabase.rpc('is_super_admin'))
          ]);
          
          if (cancelled) return;
          
          const adminStatus = (adminResult as any)?.data === true;
          const superAdminStatus = (superAdminResult as any)?.data === true;
          
          setIsAdmin(adminStatus);
          setIsSuperAdmin(superAdminStatus);
          
          debugLog('auth', `Role: ${normalizedRole}, isAdmin(RPC): ${adminStatus}, isSuperAdmin(RPC): ${superAdminStatus}`, undefined, 'useAuth');
        } else {
          debugLog('auth', 'Not authenticated, using default user role', undefined, 'useAuth');
          setRole('user');
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setIsDealer(false);
        }
      } catch (error) {
        debugError('auth', 'Error in useEffect:', error, 'useAuth');
        setRole('user');
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsDealer(false);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    });
    
    return () => { cancelled = true; };
  }, [isAuthenticated, user, isLoading]);

  return {
    ...auth,
    role,
    isAdmin,
    isSuperAdmin,
    isDealer,
    loading: loading || isLoading
  };
};

export default useAuth;
